import { z } from "zod";
import {
  CRM_TASK_PRIORITY_VALUES,
  CRM_TASK_STATUS_VALUES,
  CRM_TASK_TYPE_VALUES,
  ENGAGEMENT_STATUSES,
} from "@ease/shared";
export type { EngagementStatus } from "@ease/shared";

// ---------------------------------------------------------------------------
// Enums (as const pattern)
// ---------------------------------------------------------------------------

export const ENTITY_TYPES = ["person", "deal", "company", "task", "activity"] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

/** Fallback stages — projects MUST provide their own via createCRMEngine({ dealStages }) */
export const DEAL_STAGES_DEFAULT = ["new", "contacted", "qualified", "proposal", "won", "lost"] as const;

export const ACTIVITY_TYPES = ["call", "email", "meeting", "note", "sms", "whatsapp", "task_completed", "webinar"] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const ACTIVITY_DIRECTIONS = ["inbound", "outbound"] as const;
export type ActivityDirection = (typeof ACTIVITY_DIRECTIONS)[number];

export const ACTIVITY_OUTCOMES = ["connected", "voicemail", "no_answer", "busy"] as const;
export type ActivityOutcome = (typeof ACTIVITY_OUTCOMES)[number];

export const TASK_TYPES = CRM_TASK_TYPE_VALUES;
export type TaskType = (typeof TASK_TYPES)[number];

export const TASK_PRIORITIES = CRM_TASK_PRIORITY_VALUES;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_STATUSES = CRM_TASK_STATUS_VALUES;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_SOURCES = ["manual", "automation", "system"] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

export const TASK_RECURRENCE_FREQUENCIES = ["daily", "weekly", "biweekly", "monthly"] as const;
export type TaskRecurrenceFrequency = (typeof TASK_RECURRENCE_FREQUENCIES)[number];

export const CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE = "__unassigned";
export const CRM_PERSON_OWNER_CUSTOM_FIELD_KEYS = ["assignedTo", "assigned_to", "ownerId", "owner_id"] as const;
export const CRM_PERSON_OWNER_LABEL_CUSTOM_FIELD_KEYS = ["assignedToLabel", "assigned_to_label", "ownerLabel", "owner_label"] as const;

export const TASK_LIFECYCLE_EVENT_TYPES = [
  "created",
  "updated",
  "assigned",
  "rescheduled",
  "completed",
  "skipped",
  "auto_completed",
  "auto_cancelled",
  "cancelled",
  "followup_created",
] as const;
export type TaskLifecycleEventType = (typeof TASK_LIFECYCLE_EVENT_TYPES)[number];

export const EVENT_SOURCES = ["user_action", "automation", "system", "api", "import"] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];

export const ACTOR_TYPES = ["user", "system", "automation", "webhook"] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const DUPLICATE_STATUSES = ["pending", "merged", "not_duplicate", "auto_merged"] as const;
export type DuplicateStatus = (typeof DUPLICATE_STATUSES)[number];

export const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "500+"] as const;
export type CompanySize = (typeof COMPANY_SIZES)[number];

export const CUSTOM_FIELD_TYPES = [
  "text", "number", "date", "boolean", "select", "multi_select",
  "url", "email", "phone", "currency", "textarea", "user",
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const CUSTOM_FIELD_ENTITY_TYPES = ["person", "deal", "company"] as const;
export type CustomFieldEntityType = (typeof CUSTOM_FIELD_ENTITY_TYPES)[number];

export const VIEW_ENTITY_TYPES = ["people", "deals", "companies", "tasks", "activities"] as const;
export type ViewEntityType = (typeof VIEW_ENTITY_TYPES)[number];

export const FILTER_OPERATORS = [
  "eq", "neq", "gt", "gte", "lt", "lte",
  "contains", "not_contains", "starts_with", "ends_with",
  "in", "not_in", "is_set", "is_not_set",
  "between", "today", "this_week", "this_month",
  "last_n_days", "next_n_days", "overdue",
] as const;
export type FilterOperator = (typeof FILTER_OPERATORS)[number];

// ---------------------------------------------------------------------------
// Core Interfaces
// ---------------------------------------------------------------------------

export { ENGAGEMENT_STATUSES };

export const LIFECYCLE_STAGES = [
  "subscriber", "lead", "opportunity", "customer", "evangelist", "other",
] as const;
export type LifecycleStage = (typeof LIFECYCLE_STAGES)[number];

export interface Person {
  id: string;
  tenantId: string;
  email: string | null;
  emailHash: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  timezone: string | null;
  avatarUrl: string | null;
  tags: string[];
  source: string | null;
  sourceDetail: string | null;
  customFields: Record<string, unknown>;
  company?: string | null;
  leadScore: number;
  engagementStatus: string;
  lifecycleStage: string;
  lastActivityAt: Date | null;
  emailVerifiedAt: Date | null;
  piiClassification: unknown;
  encryptionKeyId: string | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export interface Company {
  id: string;
  tenantId: string;
  name: string;
  domain: string | null;
  industry: string | null;
  size: CompanySize | null;
  website: string | null;
  address: Record<string, unknown> | null;
  annualRevenue: number | null;
  customFields: Record<string, unknown>;
  ownerId: string | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export interface Deal {
  id: string;
  tenantId: string;
  pipelineId: string;
  stageId: string;
  personId: string | null;
  companyId: string | null;
  title: string;
  value: number | null;
  currency: string;
  expectedCloseDate: Date | null;
  winProbability: number | null;
  closedAt: Date | null;
  lostReason: string | null;
  winReason: string | null;
  score: number;
  ownerId: string | null;
  customFields: Record<string, unknown>;
  lastHumanActivityAt: Date | null;
  momentumDirection: string | null;
  momentumScore: number | null;
  stageEnteredAt: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export interface Activity {
  id: string;
  tenantId: string;
  type: ActivityType;
  subject: string | null;
  body: string | null;
  personId: string | null;
  companyId: string | null;
  dealId: string | null;
  direction: ActivityDirection | null;
  durationSeconds: number | null;
  outcome: ActivityOutcome | null;
  isAutomated: boolean;
  metadata: Record<string, unknown>;
  performedBy: string | null;
  performedAt: Date;
  createdAt: Date;
  deletedAt: Date | null;
}

export interface Task {
  id: string;
  tenantId: string;
  title: string;
  description: string | null;
  type: TaskType;
  priority: TaskPriority;
  status: TaskStatus;
  personId: string | null;
  dealId: string | null;
  companyId: string | null;
  offerId: string | null;
  assignedTo: string | null;
  dueAt: Date | null;
  completedAt: Date | null;
  completedBy: string | null;
  source: TaskSource;
  sourceRuleId: string | null;
  template: string | null;
  notes: string | null;
  outcome: string | null;
  outcomeNotes: string | null;
  nextAction: string | null;
  followUpDate: Date | null;
  contextSnapshot: Record<string, unknown> | null;
  scriptTemplate: string | null;
  triggerType: string | null;
  triggerEventId: string | null;
  metadata: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

export interface TaskLifecycleEvent {
  id: string;
  tenantId: string;
  taskId: string;
  eventType: TaskLifecycleEventType;
  eventSource: EventSource;
  actorId: string | null;
  actorType: ActorType | null;
  previousStatus: string | null;
  nextStatus: string | null;
  previousAssignee: string | null;
  nextAssignee: string | null;
  outcome: string | null;
  reason: string | null;
  notes: string | null;
  triggerEventType: string | null;
  triggerEventId: string | null;
  metadata: Record<string, unknown>;
  createdAt: Date;
}

export interface CrmEvent {
  id: string;
  tenantId: string;
  entityType: EntityType;
  entityId: string;
  eventType: string;
  eventSource: EventSource;
  payload: Record<string, unknown>;
  schemaVersion: number;
  actorId: string;
  actorType: ActorType;
  sequenceNumber: number;
  causationId: string | null;
  correlationId: string | null;
  createdAt: Date;
}

export interface StageTransition {
  id: string;
  tenantId: string;
  dealId: string;
  fromStage: string;
  toStage: string;
  reason: string | null;
  transitionedBy: string;
  transitionSource: TaskSource;
  timeInPreviousStageSeconds: number | null;
  createdAt: Date;
}

export interface SavedView {
  id: string;
  tenantId: string;
  name: string;
  entityType: ViewEntityType;
  filters: ViewFilter[];
  sort: ViewSort[];
  columns: string[];
  grouping: Record<string, unknown> | null;
  isDefault: boolean;
  isShared: boolean;
  ownerId: string;
  pinOrder: number | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ViewFilter {
  field: string;
  operator: FilterOperator;
  value: unknown;
}

export interface ViewSort {
  field: string;
  direction: "asc" | "desc";
}

export interface CustomFieldDefinition {
  id: string;
  tenantId: string;
  entityType: CustomFieldEntityType;
  fieldKey: string;
  fieldLabel: string;
  fieldType: CustomFieldType;
  options: { value: string; label: string; color?: string }[] | null;
  required: boolean;
  showInList: boolean;
  showInCard: boolean;
  displayOrder: number;
  validation: Record<string, unknown> | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface EntityAssociation {
  id: string;
  tenantId: string;
  entityAType: EntityType;
  entityAId: string;
  entityBType: EntityType;
  entityBId: string;
  label: string;
  metadata: Record<string, unknown> | null;
  createdAt: Date;
}

export interface DuplicateCandidate {
  id: string;
  tenantId: string;
  entityType: EntityType;
  entityAId: string;
  entityBId: string;
  confidence: number;
  matchReasons: string[];
  status: DuplicateStatus;
  resolvedBy: string | null;
  resolvedAt: Date | null;
  createdAt: Date;
}

// ---------------------------------------------------------------------------
// Service Result Type
// ---------------------------------------------------------------------------

export interface ServiceResult<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface ListResult<T> {
  items: T[];
  total: number;
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Zod Input Schemas
// ---------------------------------------------------------------------------

export const createPersonSchema = z.object({
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  timezone: z.string().optional(),
  tags: z.array(z.string()).optional(),
  source: z.string().optional(),
  sourceDetail: z.string().optional(),
  customFields: z.record(z.unknown()).optional(),
  leadScore: z.number().int().min(0).optional(),
  engagementStatus: z.enum(ENGAGEMENT_STATUSES).optional(),
  lifecycleStage: z.enum(LIFECYCLE_STAGES).optional(),
});
export type CreatePersonInput = z.infer<typeof createPersonSchema>;

export const updatePersonSchema = z.object({
  email: z.string().email().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  timezone: z.string().optional(),
  tags: z.array(z.string()).optional(),
  source: z.string().optional(),
  sourceDetail: z.string().optional(),
  customFields: z.record(z.unknown()).optional(),
  leadScore: z.number().int().min(0).optional(),
  engagementStatus: z.enum(ENGAGEMENT_STATUSES).optional(),
  lifecycleStage: z.enum(LIFECYCLE_STAGES).optional(),
  lastActivityAt: z.string().datetime().optional(),
  emailVerifiedAt: z.string().datetime().optional(),
});
export type UpdatePersonInput = z.infer<typeof updatePersonSchema>;

export const createCompanySchema = z.object({
  name: z.string().min(1),
  domain: z.string().optional(),
  industry: z.string().optional(),
  size: z.enum(COMPANY_SIZES).optional(),
  website: z.string().url().optional(),
  address: z.record(z.unknown()).optional(),
  annualRevenue: z.number().optional(),
  ownerId: z.string().optional(),
  customFields: z.record(z.unknown()).optional(),
});
export type CreateCompanyInput = z.infer<typeof createCompanySchema>;

export const updateCompanySchema = z.object({
  name: z.string().min(1).optional(),
  domain: z.string().optional(),
  industry: z.string().optional(),
  size: z.enum(COMPANY_SIZES).optional(),
  website: z.string().url().optional(),
  address: z.record(z.unknown()).optional(),
  annualRevenue: z.number().optional(),
  ownerId: z.string().nullable().optional(),
  customFields: z.record(z.unknown()).optional(),
});
export type UpdateCompanyInput = z.infer<typeof updateCompanySchema>;

export const createDealSchema = z.object({
  pipelineId: z.string().default("default"),
  stageId: z.string(),
  personId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  title: z.string().min(1),
  value: z.number().optional(),
  currency: z.string().default("EUR"),
  expectedCloseDate: z.string().datetime().optional(),
  ownerId: z.string().optional(),
  customFields: z.record(z.unknown()).optional(),
});
export type CreateDealInput = z.infer<typeof createDealSchema>;

export const updateDealSchema = z.object({
  title: z.string().min(1).optional(),
  value: z.number().optional(),
  currency: z.string().optional(),
  expectedCloseDate: z.string().datetime().optional(),
  ownerId: z.string().nullable().optional(),
  customFields: z.record(z.unknown()).optional(),
  lostReason: z.string().optional(),
  winReason: z.string().optional(),
});
export type UpdateDealInput = z.infer<typeof updateDealSchema>;

export const changeDealStageSchema = z.object({
  toStage: z.string(),
  reason: z.string().optional(),
});
export type ChangeDealStageInput = z.infer<typeof changeDealStageSchema>;

export const createActivitySchema = z.object({
  type: z.enum(ACTIVITY_TYPES),
  subject: z.string().regex(/^[^\r\n\0]*$/, "Header injection characters not allowed").optional(),
  body: z.string().optional(),
  personId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  dealId: z.string().uuid().optional(),
  direction: z.enum(ACTIVITY_DIRECTIONS).optional(),
  durationSeconds: z.number().int().optional(),
  outcome: z.enum(ACTIVITY_OUTCOMES).optional(),
  isAutomated: z.boolean().default(false),
  metadata: z.record(z.unknown()).optional(),
  performedAt: z.string().datetime().optional(),
});
export type CreateActivityInput = z.infer<typeof createActivitySchema>;

export const createTaskSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  type: z.enum(TASK_TYPES).default("custom"),
  priority: z.enum(TASK_PRIORITIES).default("medium"),
  personId: z.string().uuid().optional(),
  dealId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  offerId: z.string().uuid().optional(),
  assignedTo: z.string().optional(),
  dueAt: z.string().datetime().optional(),
  source: z.enum(TASK_SOURCES).default("manual"),
  sourceRuleId: z.string().optional(),
  template: z.string().optional(),
  notes: z.string().optional(),
  outcome: z.string().optional(),
  outcomeNotes: z.string().optional(),
  nextAction: z.string().optional(),
  followUpDate: z.string().datetime().optional(),
  contextSnapshot: z.record(z.unknown()).optional(),
  scriptTemplate: z.string().optional(),
  triggerType: z.string().optional(),
  triggerEventId: z.string().uuid().optional(),
  recurrence: z.object({
    frequency: z.enum(TASK_RECURRENCE_FREQUENCIES),
    interval: z.number().int().min(1).max(365).default(1),
    endsAt: z.string().datetime().nullable().optional(),
  }).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  assignedTo: z.string().nullable().optional(),
  dueAt: z.string().datetime().optional(),
  notes: z.string().optional(),
  outcome: z.string().optional(),
  outcomeNotes: z.string().optional(),
  nextAction: z.string().optional(),
  followUpDate: z.string().datetime().optional(),
  contextSnapshot: z.record(z.unknown()).optional(),
  scriptTemplate: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const completeTaskSchema = z.object({
  outcome: z.string().optional(),
  outcomeNotes: z.string().optional(),
  nextAction: z.string().optional(),
  followUpDate: z.string().datetime().optional(),
  metadata: z.record(z.unknown()).optional(),
  followUpTask: z.object({
    title: z.string().min(1).optional(),
    description: z.string().optional(),
    type: z.enum(TASK_TYPES).default("follow_up"),
    priority: z.enum(TASK_PRIORITIES).optional(),
    dueAt: z.string().datetime(),
    assignedTo: z.string().optional(),
    notes: z.string().optional(),
  }).optional(),
}).default({});
export type CompleteTaskInput = z.infer<typeof completeTaskSchema>;

export const createSavedViewSchema = z.object({
  name: z.string().min(1),
  entityType: z.enum(VIEW_ENTITY_TYPES),
  filters: z.array(z.object({
    field: z.string(),
    operator: z.enum(FILTER_OPERATORS),
    value: z.unknown(),
  })).optional(),
  sort: z.array(z.object({
    field: z.string(),
    direction: z.enum(["asc", "desc"]),
  })).optional(),
  columns: z.array(z.string()).optional(),
  grouping: z.record(z.unknown()).optional(),
  isDefault: z.boolean().default(false),
  isShared: z.boolean().default(false),
  pinOrder: z.number().int().optional(),
});
export type CreateSavedViewInput = z.infer<typeof createSavedViewSchema>;

export const createCustomFieldSchema = z.object({
  entityType: z.enum(CUSTOM_FIELD_ENTITY_TYPES),
  fieldKey: z.string().regex(/^[a-z][a-z0-9_]*$/),
  fieldLabel: z.string().min(1),
  fieldType: z.enum(CUSTOM_FIELD_TYPES),
  options: z.array(z.object({
    value: z.string(),
    label: z.string(),
    color: z.string().optional(),
  })).optional(),
  required: z.boolean().default(false),
  showInList: z.boolean().default(false),
  showInCard: z.boolean().default(false),
  displayOrder: z.number().int().default(0),
  validation: z.record(z.unknown()).optional(),
});
export type CreateCustomFieldInput = z.infer<typeof createCustomFieldSchema>;

// ---------------------------------------------------------------------------
// Pipeline Config Types
// ---------------------------------------------------------------------------

export interface PipelineConfig {
  id: string;
  name: string;
  isDefault: boolean;
  stages: PipelineStage[];
}

export interface PipelineStage {
  id: string;
  name: string;
  displayOrder: number;
  color: string;
  isWinStage: boolean;
  isLossStage: boolean;
  maxDays: number | null;
  requiredFields: string[];
  autoTasks: AutoTaskTemplate[];
}

export interface AutoTaskTemplate {
  title: string;
  type: TaskType;
  priority: TaskPriority;
  dueOffsetDays: number;
  assignTo: "deal_owner" | "specific_user";
  assignUserId?: string;
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export interface PaginationParams {
  cursor?: string;
  limit: number;
  direction?: "forward" | "backward";
}

export const DEFAULT_PAGE_LIMIT = 50;
export const MAX_PAGE_LIMIT = 200;
